/**
 * Admin Panel Component
 * Full admin dashboard: overview stats, user management, activity log,
 * credit reimbursement, promo codes, and template management.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PaymentsIcon from '@mui/icons-material/Payments';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import supabaseFlowStorageService from '../../services/SupabaseFlowStorageService';
import type { FlowTemplate } from '../../types/flow';
import './AdminPanel.css';

// â"€â"€â"€ Types â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

interface PromoCode {
  id: string;
  code: string;
  credits: number;
  description: string;
  isActive: boolean;
  maxUses: number;
  usedCount: number;
  createdAt: string;
  expiresAt?: string;
}

interface DashboardData {
  users: { total: number; newToday: number; new7d: number; active7d: number };
  credits: { used7d: number; used30d: number; purchased30d: number; inCirculation: number };
  topModels: Array<{ model_id: string; count: number; credits: number }>;
  recentActivity: Transaction[];
}

interface AdminUser {
  id: string;
  email: string;
  createdAt: string;
  lastSignIn: string | null;
  balance: number | null;
  lifetimePurchased: number;
  lifetimeUsed: number;
  lifetimeRefunded: number;
  creditsUpdatedAt: string | null;
  subscription: { status: string; planName?: string } | null;
}

interface Transaction {
  id: string;
  user_id: string;
  userEmail?: string;
  amount: number;
  type: string;
  status: string;
  description: string;
  provider?: string;
  model_id?: string;
  operation_type?: string;
  reservation_id?: string;
  balance_before: number;
  balance_after: number;
  created_at: string;
}

interface ModelPricing {
  id: string;
  provider: string;
  modelId: string;
  displayName: string;
  operationType: string;
  creditsPerUnit: number;
  unit: string;
  isActive: boolean;
  updatedAt: string;
}

type TabType = 'dashboard' | 'users' | 'activity' | 'pricing' | 'promo-codes' | 'templates' | 'organizations';

interface AdminOrganization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  is_active: boolean;
  created_at: string;
  memberCount: number;
  usageCount: number;
  creditsEquivalent: number;
}

interface AdminOrgMember {
  id: string;
  org_id: string;
  user_id: string;
  role: string;
  joined_at: string;
  email: string;
}

interface AdminOrgCredential {
  provider: string;
  label: string;
  is_active: boolean;
  updated_at: string;
}

// â"€â"€â"€ Helpers â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const fmtNum = (n: number) => n.toLocaleString();
const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
const fmtDateTime = (s: string) =>
  new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

function txColor(type: string): string {
  if (type === 'reserve') return 'var(--color-accent-error)';
  if (type === 'purchase' || type === 'bonus') return 'var(--color-accent-success)';
  if (type === 'refund') return '#42C3D7';
  return 'var(--color-text-secondary)';
}

function txSign(tx: Transaction): string {
  return tx.amount > 0 ? `+${fmtNum(tx.amount)}` : fmtNum(tx.amount);
}

// â"€â"€â"€ Stat Card â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const StatCard: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <div className={`stat-card${accent ? ' stat-card-accent' : ''}`}>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
  </div>
);

// â"€â"€â"€ Transaction Row â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const TxRow: React.FC<{
  tx: Transaction;
  showUser: boolean;
  onReimburse?: () => void;
}> = ({ tx, showUser, onReimburse }) => (
  <div className="tx-row">
    <span className={`tx-type tx-type-${tx.type}`}>{tx.type}</span>
    <div className="tx-row-info">
      <span className="tx-desc">{tx.description || tx.model_id || '—'}</span>
      {showUser && (
        <span className="tx-user">
          {tx.userEmail || `${tx.user_id.slice(0, 14)}…`}
        </span>
      )}
    </div>
    <span className="tx-time">{fmtDateTime(tx.created_at)}</span>
    <span className="tx-amount" style={{ color: txColor(tx.type) }}>{txSign(tx)}</span>
    {onReimburse
      ? <button className="reimburse-mini-btn" onClick={onReimburse} title="Reimburse this charge"><ReplayIcon style={{ fontSize: 14 }} /></button>
      : <span />}
  </div>
);

// â"€â"€â"€ Reimburse Modal â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

interface ReimburseModalProps {
  userId: string;
  userEmail: string;
  transactionId?: string;
  defaultReason?: string;
  adminId: string;
  onConfirm: (amount: number, reason: string) => Promise<void>;
  onClose: () => void;
}

const ReimburseModal: React.FC<ReimburseModalProps> = ({
  userEmail, defaultReason, onConfirm, onClose,
}) => {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState(defaultReason || '');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const handle = async () => {
    if (amount <= 0) { setErr('Amount must be > 0'); return; }
    setLoading(true);
    setErr('');
    try { await onConfirm(amount, reason); }
    catch (e: any) { setErr(e.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="reimburse-overlay" onClick={onClose}>
      <div className="reimburse-modal" onClick={e => e.stopPropagation()}>
        <div className="reimburse-header">
          <ReplayIcon style={{ fontSize: 16 }} />
          <span>Reimburse Credits</span>
          <button className="close-btn" onClick={onClose}><CloseIcon style={{ fontSize: 16 }} /></button>
        </div>
        <div className="reimburse-body">
          <div className="reimburse-email">{userEmail}</div>
          <label>Credits to add</label>
          <input
            type="number"
            min="1"
            value={amount || ''}
            onChange={e => setAmount(parseInt(e.target.value) || 0)}
            placeholder="e.g. 500"
            className="credit-input"
            autoFocus
          />
          <label>Reason</label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Generation failed - refund"
            className="credit-input"
          />
          {err && <div className="reimburse-error">{err}</div>}
        </div>
        <div className="reimburse-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="confirm-btn"
            onClick={handle}
            disabled={loading || amount <= 0}
          >
            {loading ? 'Processing...' : `Add ${amount > 0 ? fmtNum(amount) : '?'} credits`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── User Email Autocomplete ─────────────────────────────────────────────────

const UserEmailAutocomplete: React.FC<{
  value: string;
  onChange: (email: string) => void;
  userId: string;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, userId, placeholder = 'Search by email...', className = 'org-input' }) => {
  const [suggestions, setSuggestions] = useState<Array<{ id: string; email: string }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/users/search?q=${encodeURIComponent(q)}`, {
        headers: { 'x-user-id': userId },
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.users || []);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [userId]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    setShowSuggestions(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 250);
  };

  const handleSelect = (email: string) => {
    onChange(email);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="email-autocomplete" style={{ position: 'relative', flex: 1, minWidth: 200 }}>
      <input
        type="text"
        value={value}
        onChange={handleInput}
        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="email-suggestions">
          {suggestions.map((s) => (
            <button key={s.id} className="email-suggestion-item" onClick={() => handleSelect(s.email)}>
              {s.email}
            </button>
          ))}
          {loading && <div className="email-suggestion-loading">Searching...</div>}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [error, setError] = useState<string | null>(null);

  // Dashboard
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [dashLoading, setDashLoading] = useState(false);

  // Users
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersOffset, setUsersOffset] = useState(0);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userTx, setUserTx] = useState<Record<string, Transaction[]>>({});
  const [userTxLoading, setUserTxLoading] = useState<string | null>(null);

  // Activity
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txOffset, setTxOffset] = useState(0);
  const [txTypeFilter, setTxTypeFilter] = useState('');

  // Promo codes
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [generateCredits, setGenerateCredits] = useState(1000);
  const [generateDescription, setGenerateDescription] = useState('');
  const [generating, setGenerating] = useState(false);

  // Pricing
  const [pricingModels, setPricingModels] = useState<ModelPricing[]>([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingFilter, setPricingFilter] = useState('');
  const [pricingTypeFilter, setPricingTypeFilter] = useState('');
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<ModelPricing>>({});
  const [savingModel, setSavingModel] = useState<string | null>(null);
  const [showAddModel, setShowAddModel] = useState(false);
  const [newModel, setNewModel] = useState({ provider: 'falai', modelId: '', displayName: '', creditsPerUnit: 0, unit: 'generation', operationType: 'image' });

  // Templates
  const [templates, setTemplates] = useState<FlowTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Organizations
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [orgMembers, setOrgMembers] = useState<Record<string, AdminOrgMember[]>>({});
  const [orgCredentials, setOrgCredentials] = useState<Record<string, AdminOrgCredential[]>>({});
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', ownerEmail: '' });
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [addMemberInputs, setAddMemberInputs] = useState<Record<string, { email: string; role: string }>>({});
  const [credentialInputs, setCredentialInputs] = useState<Record<string, { provider: string; apiKey: string; label: string }>>({});

  // Reimburse
  const [reimburseTarget, setReimburseTarget] = useState<{
    userId: string; userEmail: string; transactionId?: string; defaultReason?: string;
  } | null>(null);
  // One idempotency key per opened reimbursement dialog: a double-click or a
  // retried submit reuses it, so the server grants once. Reopening the dialog
  // (a new target object) is a new reimbursement and gets a new key.
  const reimburseKeyRef = useRef<{ target: object; key: string } | null>(null);

  const LIMIT = 50;

  // â"€â"€â"€ Loaders â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  const loadDashboard = useCallback(async () => {
    if (!user?.id) return;
    setDashLoading(true); setError(null);
    try {
      const res = await fetch('/api/admin/dashboard', { headers: { 'x-user-id': user.id } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      setDashboard(await res.json());
    } catch (e: any) { setError(e.message); } finally { setDashLoading(false); }
  }, [user?.id]);

  const loadUsers = useCallback(async (search = usersSearch, offset = usersOffset) => {
    if (!user?.id) return;
    setUsersLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (search) params.set('search', search);
      const res = await fetch(`/api/admin/users?${params}`, { headers: { 'x-user-id': user.id } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      setUsers(data.users || []); setUsersTotal(data.total || 0);
    } catch (e: any) { setError(e.message); } finally { setUsersLoading(false); }
  }, [user?.id, usersSearch, usersOffset]);

  const loadUserTx = useCallback(async (uid: string) => {
    if (!user?.id) return;
    setUserTxLoading(uid);
    try {
      const params = new URLSearchParams({ userId: uid, limit: '20' });
      const res = await fetch(`/api/admin/transactions?${params}`, { headers: { 'x-user-id': user.id } });
      if (res.ok) {
        const data = await res.json();
        setUserTx(prev => ({ ...prev, [uid]: data.transactions || [] }));
      }
    } finally { setUserTxLoading(null); }
  }, [user?.id]);

  const loadTransactions = useCallback(async (offset = txOffset, typeFilter = txTypeFilter) => {
    if (!user?.id) return;
    setTxLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (typeFilter) params.set('type', typeFilter);
      const res = await fetch(`/api/admin/transactions?${params}`, { headers: { 'x-user-id': user.id } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      setTransactions((await res.json()).transactions || []);
    } catch (e: any) { setError(e.message); } finally { setTxLoading(false); }
  }, [user?.id, txOffset, txTypeFilter]);

  const loadPromoCodes = useCallback(async () => {
    if (!user?.id) return;
    setPromoLoading(true); setError(null);
    try {
      const res = await fetch('/api/admin/promo-codes', { headers: { 'x-user-id': user.id } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      setPromoCodes((await res.json()).promoCodes || []);
    } catch (e: any) { setError(e.message); } finally { setPromoLoading(false); }
  }, [user?.id]);

  const loadPricing = useCallback(async () => {
    if (!user?.id) return;
    setPricingLoading(true); setError(null);
    try {
      const res = await fetch('/api/admin/pricing', { headers: { 'x-user-id': user.id } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      setPricingModels((await res.json()).models || []);
    } catch (e: any) { setError(e.message); } finally { setPricingLoading(false); }
  }, [user?.id]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true); setError(null);
    try { setTemplates(await supabaseFlowStorageService.listTemplates()); }
    catch (e: any) { setError(e.message); } finally { setTemplatesLoading(false); }
  }, []);

  // ─── Organizations ───
  const loadOrganizations = useCallback(async () => {
    if (!user?.id) return;
    setOrgsLoading(true); setError(null);
    try {
      const res = await fetch('/api/admin/organizations', { headers: { 'x-user-id': user.id } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      setOrganizations(data.organizations || []);
    } catch (e: any) { setError(e.message); } finally { setOrgsLoading(false); }
  }, [user?.id]);

  const loadOrgMembers = useCallback(async (orgId: string) => {
    if (!user?.id) return;
    try {
      const res = await fetch(`/api/admin/organizations/${orgId}/members`, { headers: { 'x-user-id': user.id } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      setOrgMembers(prev => ({ ...prev, [orgId]: data.members || [] }));
    } catch (e: any) { setError(e.message); }
  }, [user?.id]);

  const loadOrgCredentials = useCallback(async (orgId: string) => {
    if (!user?.id) return;
    try {
      const res = await fetch(`/api/admin/organizations/${orgId}/credentials`, { headers: { 'x-user-id': user.id } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      setOrgCredentials(prev => ({ ...prev, [orgId]: data.credentials || [] }));
    } catch (e: any) { setError(e.message); }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    setError(null);
    if (activeTab === 'dashboard') loadDashboard();
    else if (activeTab === 'users') loadUsers(usersSearch, 0);
    else if (activeTab === 'activity') loadTransactions(0, txTypeFilter);
    else if (activeTab === 'pricing') loadPricing();
    else if (activeTab === 'promo-codes') loadPromoCodes();
    else if (activeTab === 'templates') loadTemplates();
    else if (activeTab === 'organizations') loadOrganizations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeTab]);

  // â"€â"€â"€ Action Handlers â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  const handleReimburse = async (amount: number, reason: string) => {
    if (!user?.id || !reimburseTarget) return;
    if (reimburseKeyRef.current?.target !== reimburseTarget) {
      reimburseKeyRef.current = { target: reimburseTarget, key: crypto.randomUUID() };
    }
    const res = await fetch('/api/admin/reimburse', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
      body: JSON.stringify({
        userId: reimburseTarget.userId, amount, reason,
        transactionId: reimburseTarget.transactionId,
        idempotencyKey: reimburseKeyRef.current.key,
      }),
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
    setReimburseTarget(null);
    if (activeTab === 'users') { loadUsers(usersSearch, usersOffset); if (expandedUser === reimburseTarget.userId) loadUserTx(reimburseTarget.userId); }
    if (activeTab === 'activity') loadTransactions(txOffset, txTypeFilter);
    if (activeTab === 'dashboard') loadDashboard();
  };

  const handleGeneratePromo = async () => {
    if (!user?.id) return;
    setGenerating(true); setError(null);
    try {
      const res = await fetch('/api/admin/promo-codes/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
        body: JSON.stringify({ credits: generateCredits, description: generateDescription || `${generateCredits} credits` }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      if (data.promoCode) {
        setPromoCodes(prev => [data.promoCode, ...prev]);
        await navigator.clipboard.writeText(data.promoCode.code);
        setCopiedCode(data.promoCode.code);
        setTimeout(() => setCopiedCode(null), 2000);
        setGenerateDescription('');
      }
    } catch (e: any) { setError(e.message); } finally { setGenerating(false); }
  };

  const handleDeactivatePromo = async (code: string) => {
    if (!user?.id || !confirm(`Deactivate code ${code}?`)) return;
    try {
      await fetch(`/api/admin/promo-codes/${code}/deactivate`, { method: 'POST', headers: { 'x-user-id': user.id } });
      setPromoCodes(prev => prev.map(pc => pc.code === code ? { ...pc, isActive: false } : pc));
    } catch (e: any) { setError((e as Error).message); }
  };

  const handleDeleteTemplate = async (id: string, name: string) => {
    if (!confirm(`Delete template "${name}"?`)) return;
    try {
      await supabaseFlowStorageService.deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.metadata.id !== id));
    } catch (e: any) { setError((e as Error).message); }
  };

  const handleSaveModel = async (model: ModelPricing) => {
    if (!user?.id) return;
    setSavingModel(model.id);
    try {
      const res = await fetch(`/api/admin/pricing/${model.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
        body: JSON.stringify(editValues),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      const updated = await res.json();
      setPricingModels(prev => prev.map(m => m.id === model.id ? { ...m, ...updated } : m));
      setEditingModel(null);
      setEditValues({});
    } catch (e: any) { setError(e.message); } finally { setSavingModel(null); }
  };

  const handleAddModel = async () => {
    if (!user?.id) return;
    setSavingModel('new');
    try {
      const res = await fetch('/api/admin/pricing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
        body: JSON.stringify(newModel),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      const created = await res.json();
      setPricingModels(prev => [...prev, created].sort((a, b) => `${a.provider}${a.modelId}`.localeCompare(`${b.provider}${b.modelId}`)));
      setShowAddModel(false);
      setNewModel({ provider: 'falai', modelId: '', displayName: '', creditsPerUnit: 0, unit: 'generation', operationType: 'image' });
    } catch (e: any) { setError(e.message); } finally { setSavingModel(null); }
  };

  const handleToggleActive = async (model: ModelPricing) => {
    if (!user?.id) return;
    try {
      const res = await fetch(`/api/admin/pricing/${model.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
        body: JSON.stringify({ isActive: !model.isActive }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      setPricingModels(prev => prev.map(m => m.id === model.id ? { ...m, isActive: !m.isActive } : m));
    } catch (e: any) { setError(e.message); }
  };

  const toggleUserExpand = (uid: string) => {
    if (expandedUser === uid) { setExpandedUser(null); return; }
    setExpandedUser(uid);
    if (!userTx[uid]) loadUserTx(uid);
  };

  // â"€â"€â"€ Tab renderers â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  const renderDashboard = () => {
    if (dashLoading) return <div className="admin-loading">Loading dashboard...</div>;
    if (!dashboard) return null;
    const { users: u, credits: c, topModels, recentActivity } = dashboard;
    return (
      <div className="dashboard-tab">
        <div className="section-title"><GroupIcon style={{ fontSize: 14 }} />Users</div>
        <div className="stats-grid">
          <StatCard label="Total users" value={fmtNum(u.total)} />
          <StatCard label="New today" value={fmtNum(u.newToday)} />
          <StatCard label="New (7d)" value={fmtNum(u.new7d)} />
          <StatCard label="Active (7d)" value={fmtNum(u.active7d)} accent />
        </div>

        <div className="section-title"><PaymentsIcon style={{ fontSize: 14 }} />Credits</div>
        <div className="stats-grid">
          <StatCard label="Used (7d)" value={fmtNum(c.used7d)} />
          <StatCard label="Used (30d)" value={fmtNum(c.used30d)} />
          <StatCard label="Purchased (30d)" value={fmtNum(c.purchased30d)} accent />
          <StatCard label="In circulation" value={fmtNum(c.inCirculation)} />
        </div>

        {topModels.length > 0 && (
          <>
            <div className="section-title"><TrendingUpIcon style={{ fontSize: 14 }} />Top Models (30d)</div>
            <div className="model-table">
              <div className="model-table-header">
                <span>Model</span><span>Gens</span><span>Credits</span>
              </div>
              {topModels.map(m => (
                <div key={m.model_id} className="model-table-row">
                  <span className="model-id-cell">{m.model_id}</span>
                  <span>{fmtNum(m.count)}</span>
                  <span>{fmtNum(m.credits)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {recentActivity.length > 0 && (
          <>
            <div className="section-title"><TrendingUpIcon style={{ fontSize: 14 }} />Recent Activity</div>
            <div className="tx-list">
              {recentActivity.map(tx => (
                <TxRow
                  key={tx.id}
                  tx={tx}
                  showUser
                  onReimburse={tx.type === 'reserve' ? () => setReimburseTarget({
                    userId: tx.user_id,
                    userEmail: tx.userEmail || tx.user_id,
                    transactionId: tx.id,
                    defaultReason: `Refund: ${tx.description}`,
                  }) : undefined}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderUsers = () => (
    <div className="users-tab">
      <div className="search-row">
        <div className="search-input-wrap">
          <SearchIcon style={{ fontSize: 16 }} />
          <input
            type="text"
            placeholder="Search by email or user ID..."
            value={usersSearch}
            onChange={e => setUsersSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadUsers(usersSearch, 0)}
            className="search-input"
          />
        </div>
        <button className="search-btn" onClick={() => loadUsers(usersSearch, 0)}>Search</button>
      </div>
      {usersLoading
        ? <div className="admin-loading">Loading users...</div>
        : (
          <>
            <div className="list-meta">{fmtNum(usersTotal)} users{usersSearch ? ` matching "${usersSearch}"` : ''}</div>
            <div className="user-list">
              {users.map(u => (
                <div key={u.id} className="user-row">
                  <div className="user-row-main" onClick={() => toggleUserExpand(u.id)}>
                    <div className="user-row-left">
                      <AccountCircleIcon style={{ fontSize: 20 }} className="user-icon" />
                      <div className="user-row-info">
                        <span className="user-email">{u.email || <em className="muted">(no email)</em>}</span>
                        <span className="user-meta">
                          Joined {fmtDate(u.createdAt)}
                          {u.lastSignIn && <> • Last login {fmtDate(u.lastSignIn)}</>}
                          {u.subscription && (
                            <span className={`sub-badge sub-${u.subscription.status}`}>
                              {u.subscription.planName || u.subscription.status}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="user-row-right">
                      <div className="credit-chips">
                        <span className="chip chip-balance" title="Current balance">
                          {u.balance !== null ? fmtNum(u.balance) : '-'}
                        </span>
                        <span className="chip chip-used" title="Lifetime used">-{fmtNum(u.lifetimeUsed)}</span>
                      </div>
                      <button
                        className="reimburse-btn"
                        title="Reimburse credits"
                        onClick={e => { e.stopPropagation(); setReimburseTarget({ userId: u.id, userEmail: u.email }); }}
                      >
                        <ReplayIcon style={{ fontSize: 16 }} />
                      </button>
                      {expandedUser === u.id
                        ? <ExpandLessIcon style={{ fontSize: 16 }} className="expand-icon" />
                        : <ExpandMoreIcon style={{ fontSize: 16 }} className="expand-icon" />}
                    </div>
                  </div>

                  {expandedUser === u.id && (
                    <div className="user-row-expanded">
                      <div className="user-detail-chips">
                        <span className="detail-chip">Purchased: {fmtNum(u.lifetimePurchased)}</span>
                        <span className="detail-chip">Used: {fmtNum(u.lifetimeUsed)}</span>
                        <span className="detail-chip">Refunded: {fmtNum(u.lifetimeRefunded)}</span>
                        <span className="detail-chip muted">ID: {u.id.slice(0, 8)}...</span>
                      </div>
                      {userTxLoading === u.id
                        ? <div className="admin-loading small">Loading...</div>
                        : userTx[u.id]
                          ? userTx[u.id].length === 0
                            ? <div className="empty-state small">No transactions yet</div>
                            : (
                              <div className="tx-list">
                                {userTx[u.id].map(tx => (
                                  <TxRow
                                    key={tx.id}
                                    tx={tx}
                                    showUser={false}
                                    onReimburse={tx.type === 'reserve' ? () => setReimburseTarget({
                                      userId: u.id,
                                      userEmail: u.email,
                                      transactionId: tx.id,
                                      defaultReason: `Refund: ${tx.description}`,
                                    }) : undefined}
                                  />
                                ))}
                              </div>
                            )
                          : null
                      }
                    </div>
                  )}
                </div>
              ))}
            </div>
            {users.length === LIMIT && (
              <button className="load-more-btn" onClick={() => {
                const next = usersOffset + LIMIT;
                setUsersOffset(next);
                loadUsers(usersSearch, next);
              }}>Load more</button>
            )}
          </>
        )
      }
    </div>
  );

  const renderActivity = () => (
    <div className="activity-tab">
      <div className="filter-row">
        <label className="filter-label">Type:</label>
        <select
          className="filter-select"
          value={txTypeFilter}
          onChange={e => { setTxTypeFilter(e.target.value); loadTransactions(0, e.target.value); }}
        >
          <option value="">All</option>
          <option value="reserve">reserve (usage)</option>
          <option value="purchase">purchase</option>
          <option value="refund">refund</option>
          <option value="bonus">bonus</option>
        </select>
        <button className="search-btn" onClick={() => loadTransactions(0, txTypeFilter)}>Refresh</button>
      </div>
      {txLoading
        ? <div className="admin-loading">Loading transactions...</div>
        : (
          <>
            <div className="tx-list">
              {transactions.length === 0
                ? <div className="empty-state">No transactions found</div>
                : transactions.map(tx => (
                  <TxRow
                    key={tx.id}
                    tx={tx}
                    showUser
                    onReimburse={tx.type === 'reserve' ? () => setReimburseTarget({
                      userId: tx.user_id,
                      userEmail: tx.userEmail || tx.user_id,
                      transactionId: tx.id,
                      defaultReason: `Refund: ${tx.description}`,
                    }) : undefined}
                  />
                ))
              }
            </div>
            {transactions.length === LIMIT && (
              <button className="load-more-btn" onClick={() => {
                const next = txOffset + LIMIT;
                setTxOffset(next);
                loadTransactions(next, txTypeFilter);
              }}>Load more</button>
            )}
          </>
        )
      }
    </div>
  );

  const renderPromoCodes = () => (
    <>
      <div className="generate-section">
        <h3>Generate Promo Code</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Credits</label>
            <input
              type="number"
              value={generateCredits}
              onChange={e => setGenerateCredits(parseInt(e.target.value) || 0)}
              min="1" step="100"
              className="credits-input"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={generateDescription}
              onChange={e => setGenerateDescription(e.target.value)}
              placeholder={`${generateCredits} credits`}
              className="description-input"
            />
          </div>
        </div>
        <button className="generate-btn" onClick={handleGeneratePromo} disabled={generating || generateCredits <= 0}>
          <AddCircleIcon style={{ fontSize: 16 }} />
          {generating ? 'Generating...' : 'Generate Code'}
        </button>
      </div>

      <div className="promo-codes-section">
        <h3>Recent Promo Codes</h3>
        {promoLoading
          ? <div className="admin-loading">Loading...</div>
          : promoCodes.length === 0
            ? <div className="empty-state">No promo codes yet</div>
            : (
              <div className="promo-codes-list">
                {promoCodes.map(pc => (
                  <div key={pc.id} className={`promo-code-item${!pc.isActive ? ' inactive' : ''}${pc.usedCount >= pc.maxUses ? ' used' : ''}`}>
                    <div className="code-info">
                      <div className="code-header">
                        <span className="code-value">{pc.code}</span>
                        <button
                          className="copy-btn"
                          onClick={() => { navigator.clipboard.writeText(pc.code); setCopiedCode(pc.code); setTimeout(() => setCopiedCode(null), 2000); }}
                        >
                          {copiedCode === pc.code
                            ? <CheckCircleIcon style={{ fontSize: 16 }} className="copied-icon" />
                            : <ContentCopyIcon style={{ fontSize: 16 }} />}
                        </button>
                      </div>
                      <div className="code-details">
                        <span className="credits-badge">{pc.credits} credits</span>
                        <span className="usage-badge">{pc.usedCount}/{pc.maxUses} uses</span>
                        {!pc.isActive && <span className="inactive-badge">Inactive</span>}
                      </div>
                      {pc.description && <div className="code-description">{pc.description}</div>}
                      <div className="code-meta">Created {new Date(pc.createdAt).toLocaleString()}</div>
                    </div>
                    {pc.isActive && pc.usedCount < pc.maxUses && (
                      <button className="deactivate-btn" onClick={() => handleDeactivatePromo(pc.code)}>Deactivate</button>
                    )}
                  </div>
                ))}
              </div>
            )
        }
      </div>
    </>
  );

  const renderPricing = () => {
    const filtered = pricingModels.filter(m => {
      if (pricingFilter && !m.modelId.toLowerCase().includes(pricingFilter.toLowerCase())
        && !m.displayName.toLowerCase().includes(pricingFilter.toLowerCase())
        && !m.provider.toLowerCase().includes(pricingFilter.toLowerCase())) return false;
      if (pricingTypeFilter && m.operationType !== pricingTypeFilter) return false;
      return true;
    });

    return (
      <div className="pricing-tab">
        <div className="pricing-controls">
          <div className="pricing-search-row">
            <div className="search-box">
              <SearchIcon style={{ fontSize: 14 }} />
              <input
                type="text"
                value={pricingFilter}
                onChange={e => setPricingFilter(e.target.value)}
                placeholder="Search models..."
                className="credit-input"
              />
            </div>
            <select
              value={pricingTypeFilter}
              onChange={e => setPricingTypeFilter(e.target.value)}
              className="credit-input"
              style={{ width: 120 }}
            >
              <option value="">All types</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="text">Text</option>
            </select>
            <button
              className="confirm-btn"
              onClick={() => setShowAddModel(!showAddModel)}
              style={{ whiteSpace: 'nowrap' }}
            >
              <AddCircleIcon style={{ fontSize: 14 }} /> Add Model
            </button>
          </div>

          {showAddModel && (
            <div className="pricing-add-form">
              <div className="pricing-add-grid">
                <div>
                  <label>Provider</label>
                  <select className="credit-input" value={newModel.provider} onChange={e => setNewModel(p => ({ ...p, provider: e.target.value }))}>
                    <option value="falai">falai</option>
                    <option value="openai">openai</option>
                    <option value="google">google</option>
                    <option value="anthropic">anthropic</option>
                    <option value="xai">xai</option>
                  </select>
                </div>
                <div>
                  <label>Model ID</label>
                  <input className="credit-input" value={newModel.modelId} onChange={e => setNewModel(p => ({ ...p, modelId: e.target.value }))} placeholder="e.g. my-model-v2" />
                </div>
                <div>
                  <label>Display Name</label>
                  <input className="credit-input" value={newModel.displayName} onChange={e => setNewModel(p => ({ ...p, displayName: e.target.value }))} placeholder="e.g. My Model V2" />
                </div>
                <div>
                  <label>Credits/Unit</label>
                  <input className="credit-input" type="number" min="0" value={newModel.creditsPerUnit || ''} onChange={e => setNewModel(p => ({ ...p, creditsPerUnit: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                  <label>Unit</label>
                  <select className="credit-input" value={newModel.unit} onChange={e => setNewModel(p => ({ ...p, unit: e.target.value }))}>
                    <option value="generation">generation</option>
                    <option value="second">second</option>
                  </select>
                </div>
                <div>
                  <label>Type</label>
                  <select className="credit-input" value={newModel.operationType} onChange={e => setNewModel(p => ({ ...p, operationType: e.target.value }))}>
                    <option value="image">image</option>
                    <option value="video">video</option>
                    <option value="text">text</option>
                  </select>
                </div>
              </div>
              <div className="pricing-add-actions">
                <button className="cancel-btn" onClick={() => setShowAddModel(false)}>Cancel</button>
                <button
                  className="confirm-btn"
                  onClick={handleAddModel}
                  disabled={savingModel === 'new' || !newModel.modelId || !newModel.displayName || newModel.creditsPerUnit <= 0}
                >
                  {savingModel === 'new' ? 'Saving...' : 'Add Model'}
                </button>
              </div>
            </div>
          )}
        </div>

        {pricingLoading ? (
          <div className="admin-loading">Loading pricing...</div>
        ) : (
          <>
            <div className="pricing-summary">
              {filtered.length} model{filtered.length !== 1 ? 's' : ''} · {filtered.filter(m => m.isActive).length} active
            </div>
            <div className="pricing-table">
              <div className="pricing-table-header">
                <span>Provider</span>
                <span>Model ID</span>
                <span>Name</span>
                <span>Type</span>
                <span>Credits</span>
                <span>Unit</span>
                <span>Active</span>
                <span></span>
              </div>
              {filtered.map(m => (
                <div key={m.id} className={`pricing-table-row${!m.isActive ? ' inactive' : ''}`}>
                  {editingModel === m.id ? (
                    <>
                      <span className="pricing-cell-provider">{m.provider}</span>
                      <span className="pricing-cell-model">{m.modelId}</span>
                      <span><input className="credit-input pricing-inline-input" value={editValues.displayName ?? m.displayName} onChange={e => setEditValues(p => ({ ...p, displayName: e.target.value }))} /></span>
                      <span>
                        <select className="credit-input pricing-inline-input" value={editValues.operationType ?? m.operationType} onChange={e => setEditValues(p => ({ ...p, operationType: e.target.value }))}>
                          <option value="image">image</option>
                          <option value="video">video</option>
                          <option value="text">text</option>
                        </select>
                      </span>
                      <span><input className="credit-input pricing-inline-input" type="number" min="0" value={editValues.creditsPerUnit ?? m.creditsPerUnit} onChange={e => setEditValues(p => ({ ...p, creditsPerUnit: parseInt(e.target.value) || 0 }))} /></span>
                      <span>
                        <select className="credit-input pricing-inline-input" value={editValues.unit ?? m.unit} onChange={e => setEditValues(p => ({ ...p, unit: e.target.value }))}>
                          <option value="generation">gen</option>
                          <option value="second">sec</option>
                        </select>
                      </span>
                      <span></span>
                      <span className="pricing-actions">
                        <button className="pricing-save-btn" onClick={() => handleSaveModel(m)} disabled={savingModel === m.id} title="Save">
                          <SaveIcon style={{ fontSize: 14 }} />
                        </button>
                        <button className="pricing-cancel-btn" onClick={() => { setEditingModel(null); setEditValues({}); }} title="Cancel">
                          <CloseIcon style={{ fontSize: 14 }} />
                        </button>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="pricing-cell-provider">{m.provider}</span>
                      <span className="pricing-cell-model">{m.modelId}</span>
                      <span className="pricing-cell-name">{m.displayName}</span>
                      <span className={`pricing-type-badge pricing-type-${m.operationType}`}>{m.operationType}</span>
                      <span className="pricing-cell-credits">{m.creditsPerUnit}</span>
                      <span className="pricing-cell-unit">/{m.unit === 'generation' ? 'gen' : 'sec'}</span>
                      <span>
                        <button
                          className={`pricing-active-toggle${m.isActive ? ' active' : ''}`}
                          onClick={() => handleToggleActive(m)}
                          title={m.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {m.isActive ? 'ON' : 'OFF'}
                        </button>
                      </span>
                      <span className="pricing-actions">
                        <button className="pricing-edit-btn" onClick={() => { setEditingModel(m.id); setEditValues({}); }} title="Edit">
                          <EditIcon style={{ fontSize: 14 }} />
                        </button>
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderTemplates = () => (
    <div className="templates-section">
      <h3>Manage Templates</h3>
      <p className="section-description">Templates are created via "Save as Template" from the user menu.</p>
      {templatesLoading
        ? <div className="admin-loading">Loading templates...</div>
        : templates.length === 0
          ? <div className="empty-state">No templates created yet</div>
          : (
            <div className="templates-list">
              {templates.map(t => (
                <div key={t.metadata.id} className="template-item">
                  <div className="template-info">
                    <div className="template-name">{t.metadata.name}</div>
                    {t.metadata.description && <div className="template-description">{t.metadata.description}</div>}
                    <div className="template-meta">
                      <span className="category-badge">{t.category || 'Uncategorized'}</span>
                      <span className="node-count">{t.state?.nodes?.length || 0} nodes</span>
                      {t.usageCount !== undefined && t.usageCount > 0 && <span className="usage-count">{t.usageCount} uses</span>}
                    </div>
                    <div className="template-date">Updated {new Date(t.metadata.updatedAt).toLocaleDateString()}</div>
                  </div>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteTemplate(t.metadata.id, t.metadata.name)}
                    title="Delete template"
                  >
                    <DeleteIcon style={{ fontSize: 16 }} />
                  </button>
                </div>
              ))}
            </div>
          )
      }
    </div>
  );

  // ─── Organizations ───
  const handleCreateOrg = async () => {
    if (!user?.id || !newOrg.name || !newOrg.slug) return;
    setCreatingOrg(true); setError(null);
    try {
      const res = await fetch('/api/admin/organizations', {
        method: 'POST',
        headers: { 'x-user-id': user.id, 'Content-Type': 'application/json' },
        body: JSON.stringify(newOrg),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      setShowCreateOrg(false);
      setNewOrg({ name: '', slug: '', ownerEmail: '' });
      await loadOrganizations();
    } catch (e: any) { setError(e.message); } finally { setCreatingOrg(false); }
  };

  const handleDeleteOrg = async (orgId: string, orgName: string) => {
    if (!user?.id) return;
    if (!confirm(`Delete "${orgName}"? This cascades to all members, credentials, and usage data.`)) return;
    try {
      const res = await fetch(`/api/admin/organizations/${orgId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': user.id },
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      await loadOrganizations();
    } catch (e: any) { setError(e.message); }
  };

  const handleAddMember = async (orgId: string) => {
    if (!user?.id) return;
    const input = addMemberInputs[orgId];
    if (!input?.email) return;
    try {
      const res = await fetch(`/api/admin/organizations/${orgId}/members`, {
        method: 'POST',
        headers: { 'x-user-id': user.id, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: input.email, role: input.role || 'member' }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      setAddMemberInputs(prev => ({ ...prev, [orgId]: { email: '', role: 'member' } }));
      await loadOrgMembers(orgId);
      await loadOrganizations();
    } catch (e: any) { setError(e.message); }
  };

  const handleRemoveMember = async (orgId: string, userId: string, email: string) => {
    if (!user?.id) return;
    if (!confirm(`Remove ${email} from this org?`)) return;
    try {
      const res = await fetch(`/api/admin/organizations/${orgId}/members/${userId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': user.id },
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      await loadOrgMembers(orgId);
      await loadOrganizations();
    } catch (e: any) { setError(e.message); }
  };

  const handleSetCredential = async (orgId: string) => {
    if (!user?.id) return;
    const input = credentialInputs[orgId];
    if (!input?.provider || !input?.apiKey) return;
    try {
      const res = await fetch(`/api/admin/organizations/${orgId}/credentials`, {
        method: 'POST',
        headers: { 'x-user-id': user.id, 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      setCredentialInputs(prev => ({ ...prev, [orgId]: { provider: 'openai', apiKey: '', label: '' } }));
      await loadOrgCredentials(orgId);
    } catch (e: any) { setError(e.message); }
  };

  const handleRemoveCredential = async (orgId: string, provider: string) => {
    if (!user?.id) return;
    if (!confirm(`Remove ${provider} credential from this org?`)) return;
    try {
      const res = await fetch(`/api/admin/organizations/${orgId}/credentials/${provider}`, {
        method: 'DELETE',
        headers: { 'x-user-id': user.id },
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
      await loadOrgCredentials(orgId);
    } catch (e: any) { setError(e.message); }
  };

  const toggleOrgExpansion = (orgId: string) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
    } else {
      setExpandedOrg(orgId);
      if (!orgMembers[orgId]) loadOrgMembers(orgId);
      if (!orgCredentials[orgId]) loadOrgCredentials(orgId);
      if (!addMemberInputs[orgId]) setAddMemberInputs(prev => ({ ...prev, [orgId]: { email: '', role: 'member' } }));
      if (!credentialInputs[orgId]) setCredentialInputs(prev => ({ ...prev, [orgId]: { provider: 'openai', apiKey: '', label: '' } }));
    }
  };

  const renderOrganizations = () => (
    <div className="organizations-tab">
      <div className="orgs-header">
        <h3>Organizations</h3>
        <button className="add-btn" onClick={() => setShowCreateOrg(true)}>
          <AddCircleIcon style={{ fontSize: 14 }} /> New Organization
        </button>
      </div>

      {showCreateOrg && (
        <div className="create-org-form">
          <input
            type="text"
            placeholder="Organization name (e.g. Avocados from Mexico)"
            value={newOrg.name}
            onChange={e => setNewOrg(prev => ({ ...prev, name: e.target.value }))}
            className="org-input"
          />
          <input
            type="text"
            placeholder="Slug (e.g. avocados-from-mexico)"
            value={newOrg.slug}
            onChange={e => setNewOrg(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
            className="org-input"
          />
          <UserEmailAutocomplete
            value={newOrg.ownerEmail}
            onChange={email => setNewOrg(prev => ({ ...prev, ownerEmail: email }))}
            userId={user?.id || ''}
            placeholder="Owner email (optional — defaults to you)"
          />
          <div className="create-org-actions">
            <button onClick={handleCreateOrg} disabled={creatingOrg || !newOrg.name || !newOrg.slug} className="save-btn">
              {creatingOrg ? 'Creating…' : 'Create'}
            </button>
            <button onClick={() => { setShowCreateOrg(false); setNewOrg({ name: '', slug: '', ownerEmail: '' }); }} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}

      {orgsLoading
        ? <div className="admin-loading">Loading organizations…</div>
        : organizations.length === 0
          ? <div className="empty-state">No organizations yet. Create one to onboard an enterprise client.</div>
          : (
            <div className="orgs-list">
              {organizations.map(org => (
                <div key={org.id} className={`org-item${expandedOrg === org.id ? ' expanded' : ''}`}>
                  <div className="org-row" onClick={() => toggleOrgExpansion(org.id)}>
                    <div className="org-info">
                      <div className="org-name">{org.name}</div>
                      <div className="org-slug">{org.slug} · {org.plan}</div>
                    </div>
                    <div className="org-stats">
                      <span className="org-stat"><GroupIcon style={{ fontSize: 12 }} /> {org.memberCount}</span>
                      <span className="org-stat"><TrendingUpIcon style={{ fontSize: 12 }} /> {fmtNum(org.usageCount)}</span>
                    </div>
                    <span className="org-date">{fmtDate(org.created_at)}</span>
                    <button
                      className="org-delete-btn"
                      onClick={(e) => { e.stopPropagation(); handleDeleteOrg(org.id, org.name); }}
                      title="Delete organization"
                    >
                      <DeleteIcon style={{ fontSize: 14 }} />
                    </button>
                    {expandedOrg === org.id ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
                  </div>

                  {expandedOrg === org.id && (
                    <div className="org-detail">
                      {/* Members */}
                      <div className="org-section">
                        <h4>Members</h4>
                        <div className="add-member-row">
                          <UserEmailAutocomplete
                            value={addMemberInputs[org.id]?.email || ''}
                            onChange={email => setAddMemberInputs(prev => ({
                              ...prev,
                              [org.id]: { ...(prev[org.id] || { email: '', role: 'member' }), email },
                            }))}
                            userId={user?.id || ''}
                            placeholder="Search user by email..."
                          />
                          <select
                            value={addMemberInputs[org.id]?.role || 'member'}
                            onChange={e => setAddMemberInputs(prev => ({
                              ...prev,
                              [org.id]: { ...(prev[org.id] || { email: '', role: 'member' }), role: e.target.value },
                            }))}
                            className="org-select"
                          >
                            <option value="owner">Owner</option>
                            <option value="admin">Admin</option>
                            <option value="member">Member</option>
                            <option value="viewer">Viewer</option>
                          </select>
                          <button onClick={() => handleAddMember(org.id)} className="save-btn">
                            <AddCircleIcon style={{ fontSize: 14 }} /> Add
                          </button>
                        </div>
                        {(orgMembers[org.id] || []).length === 0
                          ? <div className="empty-state-mini">No members yet</div>
                          : (
                            <div className="members-list">
                              {(orgMembers[org.id] || []).map(m => (
                                <div key={m.id} className="member-row">
                                  <span className="member-email">{m.email || `${m.user_id.slice(0, 14)}…`}</span>
                                  <span className={`member-role role-${m.role}`}>{m.role}</span>
                                  <span className="member-date">{fmtDate(m.joined_at)}</span>
                                  <button
                                    className="member-remove-btn"
                                    onClick={() => handleRemoveMember(org.id, m.user_id, m.email)}
                                    title="Remove member"
                                  >
                                    <DeleteIcon style={{ fontSize: 12 }} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )
                        }
                      </div>

                      {/* BYOK Credentials */}
                      <div className="org-section">
                        <h4>BYOK Credentials</h4>
                        <p className="section-description-mini">API keys for this org. Stored encrypted. Generation costs go to the client.</p>
                        <div className="add-credential-row">
                          <select
                            value={credentialInputs[org.id]?.provider || 'openai'}
                            onChange={e => setCredentialInputs(prev => ({
                              ...prev,
                              [org.id]: { ...(prev[org.id] || { provider: 'openai', apiKey: '', label: '' }), provider: e.target.value },
                            }))}
                            className="org-select"
                          >
                            <option value="openai">OpenAI</option>
                            <option value="anthropic">Anthropic</option>
                            <option value="google">Google AI</option>
                            <option value="falai">fal.ai</option>
                            <option value="xai">xAI (Grok)</option>
                            <option value="piapi">PiAPI</option>
                            <option value="vertexai">Vertex AI</option>
                          </select>
                          <input
                            type="password"
                            placeholder="API key (sk-...)"
                            value={credentialInputs[org.id]?.apiKey || ''}
                            onChange={e => setCredentialInputs(prev => ({
                              ...prev,
                              [org.id]: { ...(prev[org.id] || { provider: 'openai', apiKey: '', label: '' }), apiKey: e.target.value },
                            }))}
                            className="org-input"
                          />
                          <input
                            type="text"
                            placeholder="Label (optional)"
                            value={credentialInputs[org.id]?.label || ''}
                            onChange={e => setCredentialInputs(prev => ({
                              ...prev,
                              [org.id]: { ...(prev[org.id] || { provider: 'openai', apiKey: '', label: '' }), label: e.target.value },
                            }))}
                            className="org-input"
                          />
                          <button onClick={() => handleSetCredential(org.id)} className="save-btn">
                            <SaveIcon style={{ fontSize: 14 }} /> Save
                          </button>
                        </div>
                        {(orgCredentials[org.id] || []).length === 0
                          ? <div className="empty-state-mini">No BYOK credentials configured</div>
                          : (
                            <div className="credentials-list">
                              {(orgCredentials[org.id] || []).map(c => (
                                <div key={c.provider} className="credential-row">
                                  <span className="credential-provider">{c.provider}</span>
                                  <span className="credential-label">{c.label || '—'}</span>
                                  <span className={`credential-status${c.is_active ? ' active' : ''}`}>
                                    {c.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                  <span className="credential-date">{fmtDate(c.updated_at)}</span>
                                  <button
                                    className="credential-remove-btn"
                                    onClick={() => handleRemoveCredential(org.id, c.provider)}
                                    title="Remove credential"
                                  >
                                    <DeleteIcon style={{ fontSize: 12 }} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )
                        }
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
      }
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────────

  const tabs: Array<{ id: TabType; label: string; icon: React.ReactNode }> = [
    { id: 'dashboard', label: 'Overview', icon: <DashboardIcon style={{ fontSize: 14 }} /> },
    { id: 'users', label: 'Users', icon: <GroupIcon style={{ fontSize: 14 }} /> },
    { id: 'organizations', label: 'Orgs', icon: <BusinessIcon style={{ fontSize: 14 }} /> },
    { id: 'activity', label: 'Activity', icon: <TrendingUpIcon style={{ fontSize: 14 }} /> },
    { id: 'pricing', label: 'Pricing', icon: <MonetizationOnIcon style={{ fontSize: 14 }} /> },
    { id: 'promo-codes', label: 'Promo', icon: <CardGiftcardIcon style={{ fontSize: 14 }} /> },
    { id: 'templates', label: 'Templates', icon: <BookmarksIcon style={{ fontSize: 14 }} /> },
  ];

  return (
    <>
      <div className="admin-dashboard-shell">
        <div className="admin-panel wide">
          <div className="admin-panel-header admin-dashboard-header">
            <div className="header-title">
              <DashboardIcon style={{ fontSize: 16 }} />
              <span>Admin Dashboard</span>
            </div>
          </div>

          <div className="admin-panel-tabs">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`tab-btn${activeTab === t.id ? ' active' : ''}`}
                onClick={() => { setActiveTab(t.id); setError(null); }}
              >
                {t.icon}{t.label}
              </button>
            ))}
          </div>

          <div className="admin-panel-content">
            {error && <div className="error-message">{error}</div>}
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'users' && renderUsers()}
            {activeTab === 'organizations' && renderOrganizations()}
            {activeTab === 'activity' && renderActivity()}
            {activeTab === 'pricing' && renderPricing()}
            {activeTab === 'promo-codes' && renderPromoCodes()}
            {activeTab === 'templates' && renderTemplates()}
          </div>
        </div>
      </div>

      {reimburseTarget && (
        <ReimburseModal
          userId={reimburseTarget.userId}
          userEmail={reimburseTarget.userEmail}
          transactionId={reimburseTarget.transactionId}
          defaultReason={reimburseTarget.defaultReason}
          adminId={user?.id || ''}
          onConfirm={handleReimburse}
          onClose={() => setReimburseTarget(null)}
        />
      )}
    </>
  );
};

export default AdminPanel;
